import { css as cssClassName } from '@emotion/css';
import styled from '@emotion/styled';
import Editor from '@monaco-editor/react';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMove } from 'react-aria';

import { ToastManager } from '../../ToastManager';

import { useEventCallback } from '../../hooks/useEventCallback';
import { useI18n } from '../../hooks/useI18nFormatters';
import { useApi } from '../../store/SelectedHomeyStore.hooks';
import { useWebAppSettings } from '../../store/web-app-settings/useWebAppSettings';

import { su } from '../../theme/functions/su';
import { glyph } from '../../theme/glyph';
import { theme } from '../../theme/theme';

import { configureHomeyScriptEditor } from './configureHomeyScriptEditor';
import { DeprecatedLabel } from './DeprecatedLabel';
import { ScriptOutput } from './ScriptOutput';
import { scriptsStore } from './ScriptsStore';
import { ScriptTypeCheckingToggle } from './ScriptTypeCheckingToggle';
import { useScriptRouteId } from './useScriptRouteId';
import { useScripts } from './useScripts';
import { useScriptUnsavedChangesPrompt } from './useScriptUnsavedChangesPrompt';

export function ScriptEditor(props) {
  const { api } = useApi();
  const { i18n } = useI18n();
  const { settings } = useWebAppSettings();

  const { scriptRouteId } = useScriptRouteId();
  const scripts = useScripts();
  const script = useMemo(() => {
    return scripts.byId?.[scriptRouteId] ?? null;
  }, [scripts.byId, scriptRouteId]);

  const editorInstanceRef = props.editorInstanceRef;
  const monacoRef = useRef();
  const loadedScriptRef = useRef(null);
  const draftRef = useRef(null);

  const [editorInstance, setEditorInstance] = useState(null);

  useEffect(() => {
    if (editorInstance != null && api != null) {
      configureHomeyScriptEditor(monacoRef.current, api, {
        isTypeCheckingEnabled: settings.homeyScriptTypeChecking === true,
      }).catch(console.error);
    }
  }, [api, editorInstance, settings.homeyScriptTypeChecking]);

  const { prompt } = useScriptUnsavedChangesPrompt({ editorInstanceRef });

  useEffect(() => {
    const editor = editorInstance;

    if (script?.savedCode == null || editor == null) {
      return;
    }

    const loadedScript = loadedScriptRef.current;
    const currentValue = editor.getValue();
    const isSameScript = loadedScript?.id === script.id && loadedScript?.homeyId === api?.id;
    const hasUnsavedChanges = isSameScript && currentValue !== loadedScript.code;

    if (hasUnsavedChanges === false && currentValue !== script.savedCode) {
      editor.setValue(script.savedCode);
      editor.revealLine(0);
    }

    loadedScriptRef.current = { id: script.id, homeyId: api?.id, code: script.savedCode };
    draftRef.current = { id: script.id, homeyId: api?.id, code: editor.getValue() };
  }, [script, editorInstance, api?.id]);

  const handleEditorChange = useEventCallback((code) => {
    draftRef.current = { id: scriptRouteId, homeyId: api?.id, code };
  });

  function handleRequestSaveScript() {
    if (script?.code == null) {
      return;
    }

    scriptsStore
      .updateScript({
        id: scriptRouteId,
        script: {
          code: editorInstanceRef.current?.getValue(),
        },
      })
      .then((script) => {
        ToastManager.add({
          icon: glyph.core.paper,
          i18n: {
            key: 'script.savedMessage',
            props: { name: script.name },
          },
        });
      })
      .catch(ToastManager.handleError);
  }

  function handleRequestTestScript() {
    if (script?.code == null) {
      return;
    }

    scriptsStore
      .testScript({
        id: script.id,
        script: {
          code: editorInstanceRef.current?.getValue(),
        },
      })
      .catch(ToastManager.handleError);
  }

  // function handleEditorValidation(markers) {
  //   // model markers
  //   markers.forEach(marker => console.log("onValidate:", marker.message));
  // }

  const ref = useRef({});
  Object.assign(ref.current, {
    onSaveScriptRequest: handleRequestSaveScript,
    onTestScriptRequest: handleRequestTestScript,
  });

  const handleEditorDidMount = useEventCallback((editorInstance, monaco) => {
    // https://microsoft.github.io/monaco-editor/api/interfaces/monaco.editor.IStandaloneCodeEditor.html

    // https://microsoft.github.io/monaco-editor/api/modules/monaco.editor.html
    // console.log(editorInstance);
    // console.log(monaco);

    editorInstanceRef.current = editorInstance;
    monacoRef.current = monaco;

    const draft = draftRef.current;
    const isSameScript = draft?.id === scriptRouteId && draft?.homeyId === api?.id;

    if (isSameScript && draft.code != null) {
      editorInstance.setValue(draft.code);
    }

    editorInstance.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
      ref.current.onSaveScriptRequest();
    });

    editorInstance.addCommand(
      monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyR,
      () => {
        ref.current.onTestScriptRequest();
      },
    );

    editorInstance.updateOptions({
      tabSize: 2,
      wordWrap: 'wordWrapColumn',
      wordWrapColumn: 120,
      codeLens: true,
      padding: {
        bottom: 0,
        top: 0,
      },
      scrollbar: {
        verticalSliderSize: 10,
        verticalScrollbarSize: 10,
      },
      minimap: { enabled: false },
      fontSize: 14,
      fontFamily: 'Roboto Mono, monospace',
      fontWeight: 400,
      // needed for overflowing popover
      fixedOverflowWidgets: true,
    });

    // editorInstance.focus();

    setEditorInstance(editorInstance);
  });

  const heightValue = useMotionValue(
    parseInt(localStorage.getItem(`homeyscript.output.height`) ?? 360, 10),
  );
  const flexBasis = useTransform(heightValue, (value) => `${value}px`);

  const move = useMove({
    onMoveStart(event) {},
    onMove(event) {
      if (heightValue.get() - event.deltaY < 20) return;
      heightValue.set(heightValue.get() - event.deltaY);
    },
    onMoveEnd(event) {
      localStorage.setItem(`homeyscript.output.height`, heightValue.get());
    },
  });

  return (
    <ScriptEditor.Root>
      <ScriptEditor.Header>
        <ScriptEditor.Name>
          {script?.name}
          <ScriptEditor.Extension>.js</ScriptEditor.Extension>
        </ScriptEditor.Name>

        {script?.version === 1 && <DeprecatedLabel />}
        <ScriptTypeCheckingToggle />
      </ScriptEditor.Header>

      <ScriptEditor.Layout>
        <ScriptEditor.Editor
          wrapperProps={{
            className: wrapperClassName,
          }}
          defaultLanguage="javascript"
          defaultValue={script?.savedCode ?? ''}
          options={{ readOnly: script?.code == null }}
          theme={settings.theme.darkMode ? 'vs-dark' : 'light'}
          loading={<div />}
          onMount={handleEditorDidMount}
          onChange={handleEditorChange}
          // onValidate={handleEditorValidation}
        />

        <ScriptEditor.OutputContainer style={{ '--flex-basis': flexBasis }}>
          <ScriptEditor.Divider {...move.moveProps}>
            {i18n.messageFormatter('script.console')}
          </ScriptEditor.Divider>

          <ScriptOutput scriptId={scriptRouteId} />
        </ScriptEditor.OutputContainer>
      </ScriptEditor.Layout>

      {prompt}
    </ScriptEditor.Root>
  );
}

ScriptEditor.Root = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  padding: ${su(1, 6, 2, 4)};
`;

ScriptEditor.OutputContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  flex: 0 0 var(--flex-basis);
`;

ScriptEditor.Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${su(1)};
`;

ScriptEditor.Divider = styled.div`
  display: flex;
  align-items: center;

  flex: 0 0 auto;
  padding: ${su(2)} 0;

  color: ${theme.color.text_light};

  &:hover,
  &:active {
    cursor: ns-resize;
  }

  &::after {
    margin-inline-start: ${su(2)};
    content: '';
    height: 1px;
    background-color: ${theme.color.line};
    flex-grow: 1;
  }
`;

ScriptEditor.Name = styled.h3`
  padding: ${su(1)} 0;
`;

ScriptEditor.Layout = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
`;

ScriptEditor.Extension = styled.span`
  color: ${theme.color.text_light};
`;

const wrapperClassName = cssClassName`
  display: flex;
  position: relative;
  flex: 1 1 auto;
  align-items: stretch;
  border-radius: ${theme.borderRadius.medium};
  background-color: ${theme.color.component};
  box-shadow: ${theme.boxShadow.default};
  overflow: hidden;

  .margin {
    background: transparent !important;
  }

  .monaco-editor {
    background: transparent !important;
  }

  .monaco-editor-background {
    background: transparent !important;
  }

  .overflow-guard {

  }

  .slider {
    border-radius: ${theme.borderRadius.default};
    width: 6px !important;
  }
`;

ScriptEditor.Editor = styled(Editor, {
  shouldForwardProp: (prop) => {
    // theme prop is prevented by emotion
    // on custom components
    return true;
  },
})`
  padding: ${su(2)} 0;
  position: absolute;
  direction: ltr;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;
